import { ConnectionsWizardSchemaType } from '@/features/connection-wizard/components/organisms/connection-wizard-dialog/connections-wizard-dialog';
import { Tables } from '@/lib/supabase/supabase_types_override';
import { supabase } from '@/lib/supabase/supabaseClient';

export interface AssignWizardConnectionsProps {
  crm: Tables<'crm'>;
  wizardResult: ConnectionsWizardSchemaType;
}

interface AssignWizardConnectionsResult {
  success: boolean;
  error?: string;
}

interface AssignWizardConnectionsProjectResult {
  projectId: Tables<'project'>['id'];
  projectConnectionId: Tables<'crm_project'>['id'] | null;
  result: AssignWizardConnectionsResult;
  surveys: Array<AssignWizardConnectionsSurveyResult>;
}

interface AssignWizardConnectionsSurveyResult {
  surveyId: Tables<'survey'>['id'];
  surveyConnectionId: Tables<'crm_config'>['id'] | null;
  result: AssignWizardConnectionsResult;
}

interface AssignWizardConnectionsReturn {
  projects: Array<AssignWizardConnectionsProjectResult>;
}

export const assignWizardConnections = async (
  data: AssignWizardConnectionsProps
) => {
  const results: AssignWizardConnectionsReturn = {
    projects: [],
  };
  const normalizedProjects = Object.values(data.wizardResult);
  for (const project of normalizedProjects) {
    const res = await supabase
      .from('project')
      .update({
        crm_id: data.crm.id,
        crm_system: data.crm.crm_system,
        crm_project_id: Number(project.projectConnectionId),
      })
      .eq('id', project.projectId)
      .maybeSingle();
    if (res.error) {
      results.projects.push({
        projectId: project.projectId,
        projectConnectionId: project.projectConnectionId ?? null,
        result: { success: false, error: res.error.message },
        surveys: [],
      });
      continue;
    }
    const surveysResults: AssignWizardConnectionsSurveyResult[] = [];
    const normalizedSurveys = Object.values(project.surveys || {});
    for (const survey of normalizedSurveys) {
      const surveyRes = await supabase
        .from('survey')
        .update({
          crm_id: data.crm.id,
          crm_config_id: Number(survey.surveyConnectionId),
        })
        .eq('id', survey.surveyId)
        .maybeSingle();
      if (surveyRes.error) {
        surveysResults.push({
          surveyId: survey.surveyId,
          surveyConnectionId: survey.surveyConnectionId ?? null,
          result: { success: false, error: surveyRes.error.message },
        });
        continue;
      }
      surveysResults.push({
        surveyId: survey.surveyId,
        surveyConnectionId: survey.surveyConnectionId ?? null,
        result: { success: true },
      });
    }
    results.projects.push({
      projectId: project.projectId,
      projectConnectionId: project.projectConnectionId ?? null,
      result: { success: true },
      surveys: surveysResults,
    });
  }
  if (
    results.projects.some(
      (p) =>
        p.result.success === false ||
        p.surveys.some((s) => s.result.success === false)
    )
  ) {
    const failedProjects = results.projects.filter(
      (p) =>
        p.result.success === false ||
        p.surveys.some((s) => s.result.success === false)
    );
    const failedSurveys = failedProjects.flatMap((p) =>
        p.surveys.filter((s) => s.result.success === false).map((s) => ({
            ...s,
            projectId: p.projectId,
        }))
    );
    throw new Error(`Failed to assign connections for projects: ${failedProjects.map(p => p.projectId).join(", ")}. Failed surveys: ${failedSurveys.map(s => `surveyId: ${s.surveyId} (projectId: ${s.projectId})`).join(", ")}.`);
  }
  return results;
};
